import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSearch } from '../../context/SearchContext';
import './SearchResults.css';
import { apiFetch } from '../../utils/api';
import course1 from '../../images/course1.jpg';
import course2 from '../../images/course2.jpg';
import course3 from '../../images/course3.jpg';

const SearchResults = () => {
  const { searchQuery, searchResults, isSearching } = useSearch();
  const navigate = useNavigate();
  const [enrolledCourses, setEnrolledCourses] = useState([]);

  const getCourseId = (course) => course?.id ?? course?.course_id;
  const getCourseDepartment = (course) => course?.department || course?.department_name;

  useEffect(() => {
    const fetchEnrolledCourses = async () => {
      try {
        const userEmail = localStorage.getItem('userEmail');
        if (!userEmail) return;

        const userInfo = await apiFetch(`${window._env_.API_BASE_URL}/users/info?email=${userEmail}`);
        const enrolledList = await apiFetch(`${window._env_.API_BASE_URL}/users/${userInfo.user_id}/courses`);
        const enrolledIds = enrolledList.map((course) => course.course_id);
        setEnrolledCourses(enrolledIds);
      } catch (error) {
        console.error('Error fetching enrolled courses:', error);
      }
    };

    fetchEnrolledCourses();
  }, []);

  const getCourseImage = (imageName) => {
    const imageMap = {
      'course1.jpg': course1,
      'course2.jpg': course2,
      'course3.jpg': course3
    };
    return imageMap[imageName] || course1;
  };

  // Helper: show snackbar-style popup
  const showSnackbar = (message, backgroundColor = '#4CAF50') => {
    const snackbar = document.createElement('div');
    snackbar.textContent = message;
    snackbar.style.cssText =
      `position: fixed;
       top: 20px;
       left: 50%;
       transform: translateX(-50%);
       background-color: ${backgroundColor};
       color: white;
       padding: 12px 24px;
       border-radius: 4px;
       z-index: 1000;`;
    document.body.appendChild(snackbar);
    setTimeout(() => snackbar.remove(), 3000);
  };

  const handleEnroll = async (course) => {
    try {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) {
        sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
        navigate('/Login');
        return;
      }

      const courseId = getCourseId(course);
      if (!courseId) {
        throw new Error('Course ID is missing');
      }

      const userInfo = await apiFetch(`${window._env_.API_BASE_URL}/users/info?email=${userEmail}`);
      const userId = userInfo.user_id;

      try {
        await apiFetch(`${window._env_.API_BASE_URL}/users/${userId}/enroll`, {
          method: 'POST',
          body: JSON.stringify({ course_id: courseId }),
        });
      } catch (error) {
        if (error.message && error.message.includes('already enrolled')) {
          showSnackbar('Already enrolled in the course', '#f44336');
          setEnrolledCourses(prev => (prev.includes(courseId) ? prev : [...prev, courseId]));
        } else {
          alert(`Enrollment failed: ${error.message}`);
        }
        return;
      }

      setEnrolledCourses(prev => (prev.includes(courseId) ? prev : [...prev, courseId]));

      const snackbar = document.createElement('div');
      snackbar.textContent = 'Successfully enrolled in the course';
      snackbar.style.cssText =
        'position: fixed; top: 20px; left: 50%; transform: translateX(-50%); background-color: #4CAF50; color: white; padding: 12px 24px; border-radius: 4px; z-index: 1000;';
      document.body.appendChild(snackbar);

      setTimeout(() => {
        snackbar.remove();
        navigate('/course-videos', {
          state: {
            department: getCourseDepartment(course),
            courseId,
            courseTitle: course.title || course.course_title
          }
        });
      }, 3000);
    } catch (error) {
      console.error('Enrollment error:', error);
      alert('An error occurred during enrollment. Please try again.');
    }
  };

  const handleUnenroll = async (course) => {
    try {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) {
        sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
        navigate('/Login');
        return;
      }

      const courseId = getCourseId(course);
      if (!courseId) {
        throw new Error('Course ID is missing');
      }

      const userInfo = await apiFetch(`${window._env_.API_BASE_URL}/users/info?email=${userEmail}`);
      await apiFetch(
        `${window._env_.API_BASE_URL}/users/unenroll?user_id=${userInfo.user_id}&course_id=${courseId}`,
        { method: 'DELETE' }
      );

      setEnrolledCourses(prev => prev.filter(id => id !== courseId));
      showSnackbar('Successfully unenrolled from the course', '#f44336');
    } catch (error) {
      console.error('Unenroll error:', error);
      alert('An error occurred while unenrolling. Please try again.');
    }
  };

  const handleViewDepartment = (departmentName) => {
    navigate(`/departments/${departmentName}`);
  };

  if (isSearching) {
    return (
      <div className="search-results-page">
        <div className="search-header">
          <h1>Search Results</h1>
          <p>Searching for "{searchQuery}"...</p>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Searching courses and departments...</p>
        </div>
      </div>
    );
  }

  // Separate course and department results
  const courseResults = searchResults.filter(result => result.type === 'course');
  const departmentResults = searchResults.filter(result => result.type === 'department');

  return (
    <div className="search-results-page">
      <div className="search-header">
        <h1>Search Results</h1>
        <p>
          {searchResults.length > 0
            ? `Found ${searchResults.length} result${searchResults.length > 1 ? 's' : ''} for "${searchQuery}"`
            : `No results found for "${searchQuery}"`
          }
        </p>
      </div>

      {searchResults.length > 0 && (
        <div className="search-results-container">
          {/* Department Results */}
          {departmentResults.length > 0 && (
            <div className="results-section">
              <h2>Departments ({departmentResults.length})</h2>
              <div className="department-grid">
                {departmentResults.map((dept, index) => (
                  <div className="department-card" key={`dept-${index}`}>
                    <img src={dept.image} alt={dept.name} className="department-image" />
                    <div className="card-header" style={{ backgroundColor: dept.color }}>
                      <span>{dept.courses} Courses</span>
                    </div>
                    <div className="card-body">
                      <h3>{dept.name}</h3>
                      <p>{dept.description}</p>
                      <span>{dept.learners} Learners</span>
                      <Link to={`/departments/${dept.name}`} className="explore-link">Explore →</Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Course Results */}
          {courseResults.length > 0 && (
            <div className="results-section">
              <h2>Courses ({courseResults.length})</h2>
              <div className="course-grid">
                {courseResults.map((course, index) => (
                  <div className="course-card" key={`course-${index}`}>
                    <img
                      src={getCourseImage(course.image)}
                      alt={course.title}
                      className="course-image"
                    />
                    <div className="course-info">
                      <span className="course-category">{course.category}</span>
                      <span className="course-level">{course.level}</span>
                    </div>
                    <h3 className="course-title">{course.title}</h3>
                    <p className="course-description">{course.description}</p>
                    <div className="course-department">
                      <span className="department-badge">{getCourseDepartment(course)}</span>
                      <button
                        className="view-department-btn"
                        onClick={() => handleViewDepartment(getCourseDepartment(course))}
                      >
                        View Department
                      </button>
                    </div>
                    <div className="course-enroll-section">
                      {!enrolledCourses.includes(getCourseId(course)) ? (
                        <button
                          className="enroll-btn"
                          onClick={() => handleEnroll(course)}
                        >
                          Enroll Now
                        </button>
                      ) : (
                        <>
                          <Link
                            to="/course-videos"
                            state={{
                              department: getCourseDepartment(course),
                              courseId: getCourseId(course),
                              courseTitle: course.title || course.course_title
                            }}
                            className="continue-btn"
                          >
                            Continue
                          </Link>
                          <button
                            className="unenroll-btn"
                            onClick={() => handleUnenroll(course)}
                          >
                            Unenroll
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {searchResults.length === 0 && searchQuery && (
        <div className="no-results">
          <div className="no-results-content">
            <h3>No results found</h3>
            <p>Try searching with different keywords or browse our departments to find what you're looking for.</p>
            <button
              className="browse-departments-btn"
              onClick={() => navigate('/departments')}
            >
              Browse Departments
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchResults; 